
import { useState, useEffect, useMemo } from 'react';
import { differenceInDays } from 'date-fns';
import { SchedulerDimensions } from './schedulerTypes';

export function useSchedulerDimensions(
  container: HTMLDivElement | null,
  startDate: Date,
  endDate: Date,
  tracksCount: number,
  categoryHeadersHeight: number = 0,
  customDayWidth?: number
): SchedulerDimensions | null {
  const [dimensions, setDimensions] = useState<SchedulerDimensions | null>(null);
  
  // Memoize fixed values that don't depend on container size
  const fixedValues = useMemo(() => {
    const trackHeight = 40;
    const trackHeaderWidth = 200;
    const timeHeaderHeight = 60;
    const dayWidth = customDayWidth || 120; // Use custom width or default
    const totalDays = differenceInDays(endDate, startDate) + 1;
    const gridWidth = totalDays * dayWidth;
    const trackListHeight = (tracksCount * trackHeight) + categoryHeadersHeight;
    const totalWidth = trackHeaderWidth + gridWidth;

    return {
      trackHeight,
      trackHeaderWidth,
      timeHeaderHeight,
      dayWidth,
      gridWidth,
      trackListHeight,
      totalWidth
    };
  }, [startDate, endDate, tracksCount, categoryHeadersHeight, customDayWidth]);

  useEffect(() => {
    if (!container) return;

    const calculateDimensions = () => {
      const { 
        trackHeight, 
        trackHeaderWidth, 
        timeHeaderHeight, 
        dayWidth, 
        gridWidth, 
        trackListHeight, 
        totalWidth 
      } = fixedValues;
      
      setDimensions({
        width: container.clientWidth,
        height: container.clientHeight,
        headerHeight: timeHeaderHeight,
        trackHeight,
        cellWidth: dayWidth,
        totalWidth,
        totalHeight: trackListHeight,
        trackHeaderWidth,
        timeHeaderHeight,
        gridWidth,
        trackListHeight,
        dayWidth,
      });
    };

    calculateDimensions();

    const resizeObserver = new ResizeObserver(calculateDimensions);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [container, fixedValues]);

  return dimensions;
}
